import { BookingSteps, UserProfile } from 'fitbeat.gym-core';
import React, { FunctionComponent, useState } from 'react';
import { translate } from 'react-i18next';
import { connect } from 'react-redux';
import { StyleSheet, Text, View } from 'react-native';
import { Button, ButtonSize } from '../../components';
import Theme from '../../theme';
import { createBookingServiceClient } from '../../utils/apiClientFactory';
import {
  closeFeatureDemoBookingScreen,
  setupDefaultHomeEquipmentNextPressed,
} from '../actions';
import { TranslateProp } from '../../common-types';
import { getEquipmentList, EquipmentType } from '../../utils/equipmentUtils';
import { EquipmentSelectionView } from '../../auth/components/EquipmentSelectionContainer';
import { defaultScale } from '../../utils/scaling';
import { UserDataConsumer } from '../../utils';

interface IProps {
  t: TranslateProp;
  closePressed: () => void;
  userProfile: UserProfile;
  user: {
    userProfile: UserProfile;
    userProfileService: {
      updateUserProfile: (profile: UserProfile) => Promise<UserProfile>;
    }
  };
  next: (updatedProfile?: UserProfile) => void;
}

const SetupDefaultHomeEquipmentComponent: FunctionComponent<IProps> = ({
  t,
  next,
  user,
}) => {
  const [selectedType, setSelectedType] = useState<EquipmentType | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleEquipmentSelect = (equipmentType: EquipmentType) => {
    setSelectedType(equipmentType);
  };

  const handleNextPress = async () => {
    if (!selectedType) {
      return;
    }

    setIsLoading(true);
    try {
      const equipment = getEquipmentList(selectedType);
      user.userProfile.defaultEquipment = equipment;
      await user.userProfileService.updateUserProfile(user.userProfile);
      next(user.userProfile);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.contentContainer}>
        <Text style={styles.heading}>
          {t('doYouHaveHomeEquipment')}
        </Text>
        <View style={styles.equipmentContainer}>
          <EquipmentSelectionView
            t={t}
            selectedEquipment={selectedType}
            onEquipmentSelect={handleEquipmentSelect}
          />
        </View>
        <View>
          <Button
            fontColor={Theme.WHITE}
            size={ButtonSize.MEDIUM}
            title={t('next')}
            onPress={handleNextPress}
            disabled={!selectedType || isLoading}
            isLoading={isLoading}
          />
        </View>
      </View>
    </View>
  );
};

const mapDispatchToProps = (dispatch, ownProps: IProps): Partial<IProps> => {
  const { userProfile } = ownProps;

  return {
    next: (updatedProfile = userProfile) => dispatch(setupDefaultHomeEquipmentNextPressed(updatedProfile)),
    closePressed: () =>
      dispatch(closeFeatureDemoBookingScreen(createBookingServiceClient(), BookingSteps.SetupDefaultHomeEquipment)),
  };
};

const ConnectedComponent = connect(null, mapDispatchToProps)(
  translate(['bookings'], { wait: true })(SetupDefaultHomeEquipmentComponent),
);

export const SetupDefaultHomeEquipmentStep = UserDataConsumer(ConnectedComponent);

SetupDefaultHomeEquipmentStep.getTitle = () => <></>;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: defaultScale(15),
  },
  contentContainer: {
    flex: 1,
    justifyContent: 'center',
  },
  equipmentContainer: {
    flex: 1,
  },
  heading: {
    ...Theme.textStyle.heading,
    textAlign: 'center',
  },
});
